/**
 * Item variant suffixes and enchantment labels.
 *
 * The ChestShop plugin appends a three-character base62 suffix to any item
 * carrying NBT - `Diamond Pickaxe#6qC`. Measured across 3,479 such listings:
 * 2,188 distinct suffixes, not one shared between two materials, decoding to a
 * clustered range rather than a scattered one. That is a sequential identifier,
 * so it is stable for the life of the item and is not reversible into
 * enchantments by any computation available to us.
 *
 * What makes it worth handling anyway is that it is the *same* suffix in every
 * shop: label `#5c0` once and it is a Brown Axolotl wherever it appears.
 */
const HASH_PATTERN = /^(.*)#([0-9A-Za-z]{3})$/;
export function parseVariantKey(itemKey) {
    const match = HASH_PATTERN.exec(itemKey.trim());
    if (!match)
        return { base: itemKey.trim(), hash: null };
    return { base: match[1].trim(), hash: match[2] };
}
const B62 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
/**
 * Decodes a suffix to its numeric id. Useful only for ordering and for spotting
 * when the plugin runs out of three-character space - 62^3 is 238,328 and the
 * highest id seen so far is 34,595, so roughly 15% used.
 */
export function decodeVariantId(hash) {
    if (!/^[0-9A-Za-z]{3}$/.test(hash))
        return null;
    return [...hash].reduce((acc, char) => acc * 62 + B62.indexOf(char), 0);
}
const ROMAN = {
    i: 1, ii: 2, iii: 3, iv: 4, v: 5, vi: 6, vii: 7, viii: 8, ix: 9, x: 10,
};
/** Common shorthand people actually type, mapped to the full enchantment. */
const ALIASES = {
    sharp: 'Sharpness',
    prot: 'Protection',
    eff: 'Efficiency',
    unb: 'Unbreaking',
    fort: 'Fortune',
    knock: 'Knockback',
    looting: 'Looting',
    mending: 'Mending',
    silk: 'Silk Touch',
    power: 'Power',
    punch: 'Punch',
    flame: 'Flame',
    infinity: 'Infinity',
    thorns: 'Thorns',
    depth: 'Depth Strider',
    feather: 'Feather Falling',
    fire: 'Fire Aspect',
    luck: 'Luck of the Sea',
    lure: 'Lure',
    loyalty: 'Loyalty',
    riptide: 'Riptide',
    channeling: 'Channeling',
    impaling: 'Impaling',
    multishot: 'Multishot',
    piercing: 'Piercing',
    quick: 'Quick Charge',
    respiration: 'Respiration',
    aqua: 'Aqua Affinity',
    bane: 'Bane of Arthropods',
    smite: 'Smite',
    sweeping: 'Sweeping Edge',
};
function expand(word) {
    const key = word.toLowerCase().replace(/[^a-z]/g, '');
    if (ALIASES[key])
        return ALIASES[key];
    return word
        .split(/[\s_]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
}
/**
 * Turns what someone types into structured enchantments.
 *
 * Deliberately forgiving about form - "Sharp4, Knock2", "Efficiency IV",
 * "unbreaking 3 / mending" are all things a shop owner would plausibly write,
 * and refusing them would just mean fewer labels get contributed. An entry with
 * no recognisable level (a plain "Mending", or "Brown Axolotl") is kept as a
 * name with level 0, because plenty of variants are not enchantments at all.
 */
export function parseEnchantments(input) {
    if (!input.trim())
        return [];
    return input
        .split(/[,;/|]+|\band\b/i)
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => {
        // "Sharpness 4", "Sharp4", "Efficiency IV"
        const withArabic = /^(.*?)[\s_-]*(\d{1,3})$/.exec(part);
        if (withArabic) {
            return { name: expand(withArabic[1]), level: Number(withArabic[2]) };
        }
        const withRoman = /^(.*?)[\s_-]+([ivx]{1,5})$/i.exec(part);
        if (withRoman && ROMAN[withRoman[2].toLowerCase()] != null) {
            return { name: expand(withRoman[1]), level: ROMAN[withRoman[2].toLowerCase()] };
        }
        return { name: expand(part), level: 0 };
    })
        .filter((entry) => entry.name.length > 0);
}
/** Canonical rendering, so two people typing the same thing agree. */
export function formatEnchantments(enchantments) {
    return enchantments
        .map((e) => (e.level > 0 ? `${e.name} ${e.level}` : e.name))
        .join(', ');
}
/**
 * Compares two labels for equivalence, so "Sharp4, Knock2" and
 * "Knockback 2, Sharpness 4" count as the same contribution rather than
 * competing ones.
 */
export function sameEnchantments(a, b) {
    if (a.length !== b.length)
        return false;
    const key = (list) => [...list]
        .map((e) => `${e.name.toLowerCase()}:${e.level}`)
        .sort()
        .join('|');
    return key(a) === key(b);
}
/**
 * How a labelled variant should be titled.
 *
 * Two kinds of label arrive through the same field. "Brown Axolotl" names the
 * item outright, so it stands alone. "Efficiency 4, Unbreaking 3" describes
 * what was done to a Diamond Pickaxe, and on its own it would not say what the
 * item even is - so it is bracketed onto the base name.
 *
 * The two are told apart by level: an enchantment always parses with one, and a
 * variant name never does.
 */
export function variantTitle(itemKey, enchantments) {
    const { base } = parseVariantKey(itemKey);
    if (enchantments.length === 0)
        return base;
    const allEnchantments = enchantments.every((e) => e.level > 0);
    const label = formatEnchantments(enchantments);
    return allEnchantments ? `${base} (${label})` : label;
}
//# sourceMappingURL=variants.js.map